import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { WaveBox } from "wave-box";
import {
  FragmentType,
  getFragmentData,
  graphql,
} from "../../../../api/__generated__";
import { ReactComponent as Success } from "./Success.svg";

const organizationHeaderFragment = graphql(`
  fragment OrganizationHeader on Organization {
    id
    pobosEnabled
  }
`);

interface HeaderProps {
  backgroundColor: string;
  organization: FragmentType<typeof organizationHeaderFragment>;
}

const Header = ({ backgroundColor, organization }: HeaderProps) => {
  const organizationData = getFragmentData(
    organizationHeaderFragment,
    organization,
  );

  const { pobosEnabled } = organizationData;

  const { t } = useTranslation();

  return (
    <WaveBox
      background={backgroundColor} //add opacity in hex
      fillBackground
      sx={{ margin: "2rem 0 4rem" }}
    >
      <Box
        sx={(theme) => ({
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          padding: "0 0 0 96px",
          [theme.breakpoints.down("sm")]: {
            padding: "0 6rem",
          },
          [theme.breakpoints.down(640)]: {
            padding: "0 3rem",
          },
          [theme.breakpoints.down(400)]: {
            padding: "0 18px",
          },
        })}
      >
        <Box
          sx={(theme) => ({
            alignItems: "center",
            display: "flex",
            flexDirection: "row",
            gap: "2rem",
            justifyContent: "space-between",
            maxWidth: "1300px",
            position: "relative",
            width: "100%",
            [theme.breakpoints.down("sm")]: {
              flexDirection: "column-reverse",
              gap: "1.5rem",
            },
            [theme.breakpoints.down(640)]: {
              gap: "1rem",
            },
          })}
        >
          <Box
            sx={(theme) => ({
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              maxWidth: "510px",
              padding: "2rem 0",
              [theme.breakpoints.down("sm")]: {
                maxWidth: "100%",
                textAlign: "center",
              },
            })}
          >
            <Typography
              sx={(theme) => ({
                fontSize: "clamp(1.5rem, 1.25rem + 1.875vw, 3rem)",
                marginBottom: "0.5rem",
                [theme.breakpoints.down("sm")]: {
                  fontSize: "clamp(1.5rem, 1.25rem + 1.875vw, 2.25rem)",
                },
                [theme.breakpoints.down(400)]: {
                  fontSize: "clamp(1.5rem, 1rem + 1.5vw, 2rem)",
                },
              })}
              variant="h1"
            >
              {t("organization:landing.header.title")}
            </Typography>
            <Typography
              sx={(theme) => ({
                ...theme.typography.body1,
                whiteSpace: "pre-line",
                [theme.breakpoints.down(640)]: {
                  fontSize: "1rem",
                },
              })}
            >
              {pobosEnabled
                ? t("organization:landing.header.text.pobos")
                : t("organization:landing.header.text")}
            </Typography>
          </Box>
          <Box
            component={Success}
            sx={(theme) => ({
              maxHeight: "130%",
              overflow: "visible",
              position: "absolute",
              right: 0,
              zIndex: 1,
              [theme.breakpoints.down(1200)]: {
                maxWidth: "45%",
                position: "relative",
              },
              [theme.breakpoints.down("sm")]: {
                maxWidth: "100%",
                position: "relative",
              },
              [theme.breakpoints.down(400)]: {
                maxWidth: "60%",
                position: "relative",
              },
            })}
          />
        </Box>
      </Box>
    </WaveBox>
  );
};

export default Header;
